(function () {
  /*
   * ============================================================
   * CONFIGURATION
   * ============================================================
   */

  const PRIORITY_COLLECTIONS = [
    'nuts',
    'seeds',
    'dried-fruits',
    'lentils-legumes',
    'herbs-spices',
    'savory-snacks'
  ];

  const GRID_SELECTOR =
    '#product-grid';

  const TITLE_SELECTOR =
    '.collection-hero__title';

  /*
   * Start loading the next page when the customer is
   * this many pixels from the end of the loaded feed.
   */
  const PRELOAD_DISTANCE =
    1500;

  /*
   * If the product grid hasn't changed for this long while the
   * customer is waiting near the bottom and the initial
   * collection still isn't confirmed complete, assume the
   * site's native pagination system (Ajaxinate) has stalled
   * (e.g. because "Enable filtering and sorting" - or whatever
   * theme setting controls pagination mode - was turned off)
   * and take over pagination of the initial collection
   * ourselves instead of waiting forever.
   */
  const NATIVE_PAGINATION_STALL_MS =
    2000;


  /*
   * ============================================================
   * FIND ORIGINAL SHOPIFY GRID
   * ============================================================
   */

  const originalGrid =
    document.querySelector(
      GRID_SELECTOR
    );

  if (!originalGrid) {
    console.log(
      '[multi-collection] No product grid found.'
    );

    return;
  }


  /*
   * ============================================================
   * CSS
   * ============================================================
   */

  const style =
    document.createElement(
      'style'
    );

  style.textContent = `
    /*
     * COLLECTION SECTION
     *
     * content-visibility allows the browser to skip
     * rendering work for distant off-screen collections.
     *
     * Nothing is removed from the DOM.
     */

    .multi-collection-section {
      position: relative;

      content-visibility: auto;

      contain-intrinsic-size:
        auto 1000px;
    }


    /*
     * COLLECTION TITLE
     */

    .multi-collection-sticky-title {
      position: sticky !important;

      top: var(
        --multi-collection-header-height,
        0px
      ) !important;

      z-index: 20 !important;

      display: block;

      width: 100%;

      margin: 0 0 20px;

      padding: 15px 20px;

      background:
        rgb(var(--color-background));

      text-align: left;

      box-sizing: border-box;
    }
  `;

  document.head.appendChild(
    style
  );


  /*
   * ============================================================
   * DETECT SHOPIFY STICKY HEADER HEIGHT
   * ============================================================
   */

  function updateStickyHeaderHeight() {
    const header =
      document.querySelector(
        'sticky-header'
      ) ||
      document.querySelector(
        '.header-wrapper'
      ) ||
      document.querySelector(
        '#shopify-section-header'
      );

    if (!header) {
      document.documentElement
        .style
        .setProperty(
          '--multi-collection-header-height',
          '0px'
        );

      return;
    }


    const headerRect =
      header.getBoundingClientRect();


    const headerStyle =
      window.getComputedStyle(
        header
      );


    const isSticky =
      headerStyle.position ===
        'sticky' ||
      headerStyle.position ===
        'fixed';


    /*
     * Sometimes Shopify puts the sticky positioning
     * on the parent of the actual header.
     */

    const headerParent =
      header.parentElement;


    let parentIsSticky =
      false;


    if (headerParent) {
      const parentStyle =
        window.getComputedStyle(
          headerParent
        );

      parentIsSticky =
        parentStyle.position ===
          'sticky' ||
        parentStyle.position ===
          'fixed';
    }


    /*
     * Is the header currently visible?
     */

    const headerIsVisible =
      headerRect.bottom > 0 &&
      headerRect.top <
        window.innerHeight;


    /*
     * Calculate the actual sticky offset.
     */

    const headerHeight =
      (
        isSticky ||
        parentIsSticky
      ) &&
      headerIsVisible

        ? Math.max(
            0,
            headerRect.bottom
          )

        : 0;


    document.documentElement
      .style
      .setProperty(
        '--multi-collection-header-height',
        `${Math.round(
          headerHeight
        )}px`
      );
  }


  /*
   * Initial header measurement.
   */

  updateStickyHeaderHeight();


  /*
   * Watch header size changes.
   */

  const headerResizeObserver =
    typeof ResizeObserver !==
      'undefined'

      ? new ResizeObserver(
          () => {
            updateStickyHeaderHeight();
          }
        )

      : null;


  const detectedHeader =
    document.querySelector(
      'sticky-header'
    ) ||
    document.querySelector(
      '.header-wrapper'
    ) ||
    document.querySelector(
      '#shopify-section-header'
    );


  if (
    headerResizeObserver &&
    detectedHeader
  ) {
    headerResizeObserver.observe(
      detectedHeader
    );
  }


  /*
   * Window resize.
   */

  window.addEventListener(
    'resize',
    updateStickyHeaderHeight
  );


  /*
   * ============================================================
   * INITIAL COLLECTION
   * ============================================================
   */

  const initialHandle =
    window.location.pathname
      .split('/collections/')[1]
      ?.split('/')[0]
      ?.split('?')[0];


  let activeHandle =
    initialHandle;


  /*
   * Queue of collections that have not started yet.
   */

  let remainingQueue =
    [];


  /*
   * Current collection being progressively loaded
   * by THIS script.
   *
   * The initial collection is still allowed to finish
   * through your existing Ajaxinate system.
   */

  let currentStreamRecord =
    null;


  /*
   * Loading lock.
   */

  let isLoading =
    false;


  /*
   * True after every collection has been completed.
   */

  let allCollectionsLoaded =
    false;


  /*
   * Prevent automatic loading before the customer
   * has actually started scrolling.
   */

  let userHasScrolled =
    false;


  /*
   * Total number of products in the original
   * initial collection.
   */

  let initialProductTotal =
    null;


  /*
   * Has the initial collection finished?
   */

  let initialCollectionFinished =
    false;


  /*
   * Timestamp of the last time the initial grid actually
   * changed (a product was added by Ajaxinate). Used to
   * detect whether the site's native pagination has stalled.
   */

  let lastGridMutationAt =
    Date.now();


  /*
   * ============================================================
   * FEED CONTAINER
   * ============================================================
   *
   * Every collection section is added here.
   *
   * Nothing is ever removed.
   */

  const feedContainer =
    document.createElement(
      'div'
    );

  feedContainer.id =
    'multi-collection-feed';


  /*
   * ============================================================
   * SECTION REGISTRY
   * ============================================================
   *
   * Example:
   *
   * {
   *   handle: 'nuts',
   *   title: 'Nuts',
   *   el: HTMLElement,
   *   grid: HTMLElement,
   *   nextPage: 2,
   *   complete: false
   * }
   */

  const sectionRegistry =
    [];


  /*
   * ============================================================
   * CLEAN COLLECTION TITLE
   * ============================================================
   */

  function getCleanTitle(
    titleElement
  ) {
    if (!titleElement) {
      return '';
    }


    const clone =
      titleElement.cloneNode(
        true
      );


    clone
      .querySelectorAll(
        '.visually-hidden'
      )
      .forEach(
        (element) => {
          element.remove();
        }
      );


    return clone
      .textContent
      .trim();
  }


  /*
   * ============================================================
   * DETECT INITIAL COLLECTION PRODUCT TOTAL
   * ============================================================
   */

  function detectInitialProductTotal() {
    const possibleCounters = [
      document.querySelector(
        '#ProductCountDesktop'
      ),

      document.querySelector(
        '#ProductCount'
      ),

      document.querySelector(
        '.product-count__text'
      )
    ];


    for (
      const counter
      of possibleCounters
    ) {
      if (!counter) {
        continue;
      }


      const text =
        counter
          .textContent
          .trim();


      const numbers =
        text.match(
          /\d[\d,]*/g
        );


      if (
        !numbers ||
        numbers.length === 0
      ) {
        continue;
      }


      const parsedNumbers =
        numbers.map(
          (number) =>
            parseInt(
              number.replace(
                /,/g,
                ''
              ),
              10
            )
        );


      initialProductTotal =
        Math.max(
          ...parsedNumbers
        );


      console.log(
        '[multi-collection] Initial Shopify product total:',
        initialProductTotal
      );


      return;
    }


    console.warn(
      '[multi-collection] Initial product total not detected from page text (this is expected if filtering/sorting is off - the fallback pagination takeover will still work).'
    );
  }


  /*
   * ============================================================
   * COUNT INITIAL COLLECTION PRODUCTS
   * ============================================================
   */

  function getInitialLoadedProductCount() {
    return originalGrid
      .querySelectorAll(
        ':scope > li.grid__item'
      )
      .length;
  }


  /*
   * ============================================================
   * CHECK WHETHER INITIAL COLLECTION IS COMPLETE
   * ============================================================
   */

  function isInitialCollectionComplete() {
    const loadedProducts =
      getInitialLoadedProductCount();


    /*
     * Preferred method:
     * compare against Shopify product total.
     */

    if (
      initialProductTotal !==
      null
    ) {
      return (
        loadedProducts >=
        initialProductTotal
      );
    }


    /*
     * Fallback:
     * check whether Ajaxinate still has a next-page link.
     */

    const nextLink =
      document.querySelector(
        '.infinite_next'
      );


    return !nextLink;
  }


  /*
   * ============================================================
   * UNIQUE KEY FOR A PRODUCT CARD (for de-duping)
   * ============================================================
   *
   * Used only by the fallback pagination takeover below, so it
   * never adds a product twice while catching up on whatever
   * pages the native system already loaded (or didn't).
   */

  function getProductCardKey(
    card
  ) {
    const link =
      card.querySelector(
        'a[href*="/products/"]'
      );


    if (!link) {
      return null;
    }


    const href =
      link.getAttribute(
        'href'
      );


    if (!href) {
      return null;
    }


    return href.split(
      '?'
    )[0];
  }


  /*
   * ============================================================
   * FALLBACK: TAKE OVER INITIAL COLLECTION PAGINATION
   * ============================================================
   *
   * Only runs if the native Ajaxinate system appears to have
   * stalled (see NATIVE_PAGINATION_STALL_MS). Fetches the
   * initial collection's own pages directly - the exact same
   * technique already used for every other collection - and
   * appends any product not already present in the grid.
   *
   * This makes the initial collection self-sufficient: it no
   * longer depends on whatever pagination mode your theme
   * happens to be configured with (infinite scroll, load more,
   * classic pagination, or filtering/sorting on or off).
   */

  async function takeOverInitialCollectionPagination() {
    const existingKeys =
      new Set();


    Array.from(
      originalGrid.querySelectorAll(
        ':scope > li.grid__item'
      )
    ).forEach(
      (card) => {
        const key =
          getProductCardKey(
            card
          );


        if (key) {
          existingKeys.add(
            key
          );
        }
      }
    );


    const maxPages =
      200;


    for (
      let page = 1;
      page <= maxPages;
      page++
    ) {
      const doc =
        await fetchCollectionPage(
          initialHandle,
          page
        );


      const cards =
        getProductCardsFromDocument(
          doc
        );


      if (
        cards.length ===
        0
      ) {
        console.log(
          '[multi-collection] Fallback pagination: page',
          page,
          'empty - initial collection fully loaded.'
        );

        break;
      }


      let addedThisPage =
        0;


      cards.forEach(
        (card) => {
          const key =
            getProductCardKey(
              card
            );


          if (
            key &&
            existingKeys.has(
              key
            )
          ) {
            return;
          }


          originalGrid.appendChild(
            document.importNode(
              card,
              true
            )
          );


          if (key) {
            existingKeys.add(
              key
            );
          }


          addedThisPage++;
        }
      );


      console.log(
        '[multi-collection] Fallback pagination: page',
        page,
        '- added',
        addedThisPage,
        'new product(s).'
      );


      /*
       * If we know the real total and have already reached it,
       * and this page contributed nothing new, we can stop
       * early instead of continuing to fetch empty pages.
       */

      if (
        addedThisPage === 0 &&
        initialProductTotal !==
          null &&
        getInitialLoadedProductCount() >=
          initialProductTotal
      ) {
        break;
      }
    }


    const initialRecord =
      sectionRegistry[0];


    if (initialRecord) {
      optimiseSectionImages(
        initialRecord.el
      );
    }
  }


  /*
   * ============================================================
   * OPTIMISE PRODUCT IMAGES
   * ============================================================
   *
   * Nothing is removed.
   *
   * We simply tell the browser:
   *
   * - Load images lazily.
   * - Decode images asynchronously.
   */

  function optimiseSectionImages(
    section
  ) {
    const images =
      section.querySelectorAll(
        'img'
      );


    images.forEach(
      (img) => {

        /*
         * Native browser lazy loading.
         */

        img.loading =
          'lazy';


        /*
         * Decode without unnecessarily blocking
         * the main rendering path.
         */

        img.decoding =
          'async';
      }
    );


    console.log(
      '[multi-collection] Images optimised:',
      section.dataset.collection,
      `(${images.length})`
    );
  }


  /*
   * ============================================================
   * SETUP INITIAL COLLECTION
   * ============================================================
   */

  function setupCurrentCollectionSection() {
    const currentTitle =
      document.querySelector(
        TITLE_SELECTOR
      );


    if (!currentTitle) {
      throw new Error(
        'Current collection title not found.'
      );
    }


    const titleText =
      getCleanTitle(
        currentTitle
      );


    /*
     * Hide Shopify's original title.
     */

    currentTitle.style.display =
      'none';


    /*
     * Create collection section.
     */

    const section =
      document.createElement(
        'div'
      );


    section.className =
      'multi-collection-section';


    section.dataset.collection =
      initialHandle;


    section.dataset.title =
      titleText;


    /*
     * Insert feed container where the original
     * product grid currently exists.
     */

    originalGrid
      .parentNode
      .insertBefore(
        feedContainer,
        originalGrid
      );


    /*
     * Create sticky title.
     */

    const title =
      document.createElement(
        'h1'
      );


    title.className =
      'collection-hero__title multi-collection-sticky-title';


    title.textContent =
      titleText;


    section.appendChild(
      title
    );


    /*
     * Move original Shopify grid into section.
     */

    section.appendChild(
      originalGrid
    );


    /*
     * Add section to feed.
     */

    feedContainer.appendChild(
      section
    );


    /*
     * Register initial collection.
     */

    sectionRegistry.push({
      handle:
        initialHandle,

      title:
        titleText,

      el:
        section,

      grid:
        originalGrid,

      nextPage:
        null,

      complete:
        false,

      isInitial:
        true
    });


    console.log(
      '[multi-collection] Initial section:',
      initialHandle
    );
  }


  /*
   * ============================================================
   * FETCH ALL SHOPIFY COLLECTIONS
   * ============================================================
   */

  async function fetchAllCollections() {
    let page =
      1;


    const collections =
      [];


    while (true) {
      const response =
        await fetch(
          `/collections.json?limit=250&page=${page}`
        );


      if (!response.ok) {
        throw new Error(
          `Collection list fetch failed: ${response.status}`
        );
      }


      const data =
        await response.json();


      const pageCollections =
        data.collections ||
        [];


      if (
        pageCollections.length ===
        0
      ) {
        break;
      }


      collections.push(
        ...pageCollections
      );


      if (
        pageCollections.length <
        250
      ) {
        break;
      }


      page++;
    }


    return collections;
  }


  /*
   * ============================================================
   * BUILD COLLECTION QUEUE
   * ============================================================
   */

  async function buildCollectionQueue() {
    const collections =
      await fetchAllCollections();


    const allHandles =
      collections
        .map(
          (collection) =>
            collection.handle
        )
        .filter(
          Boolean
        );


    /*
     * Priority collections first.
     */

    const priorityQueue =
      PRIORITY_COLLECTIONS.filter(
        (handle) =>
          handle !==
            initialHandle &&
          allHandles.includes(
            handle
          )
      );


    /*
     * All other collections afterwards.
     */

    const missingCollections =
      allHandles.filter(
        (handle) =>
          handle !==
            initialHandle &&
          !PRIORITY_COLLECTIONS.includes(
            handle
          )
      );


    remainingQueue = [
      ...new Set([
        ...priorityQueue,
        ...missingCollections
      ])
    ];


    console.log(
      '[multi-collection] Collection queue:',
      remainingQueue
    );
  }


  /*
   * ============================================================
   * FETCH ONE COLLECTION PAGE
   * ============================================================
   */

  async function fetchCollectionPage(
    handle,
    page
  ) {
    const url =
      `/collections/${handle}?page=${page}`;


    console.log(
      '[multi-collection] Fetching:',
      url
    );


    const response =
      await fetch(
        url
      );


    if (!response.ok) {
      throw new Error(
        `Collection fetch failed: ${response.status}`
      );
    }


    const html =
      await response.text();


    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      );
  }


  /*
   * ============================================================
   * GET PRODUCT CARDS FROM FETCHED PAGE
   * ============================================================
   */

  function getProductCardsFromDocument(
    doc
  ) {
    const fetchedGrid =
      doc.querySelector(
        GRID_SELECTOR
      );


    if (!fetchedGrid) {
      return [];
    }


    return Array.from(
      fetchedGrid.querySelectorAll(
        ':scope > li.grid__item'
      )
    );
  }


  /*
   * ============================================================
   * FIND REGISTRY INDEX
   * ============================================================
   */

  function getRegistryIndex(
    handle
  ) {
    return sectionRegistry
      .findIndex(
        (record) =>
          record.handle ===
          handle
      );
  }


  /*
   * ============================================================
   * UPDATE ACTIVE COLLECTION
   * ============================================================
   */

  function updateActiveCollection() {
    const sections =
      feedContainer
        .querySelectorAll(
          '.multi-collection-section'
        );


    const headerOffset =
      parseFloat(
        getComputedStyle(
          document.documentElement
        )
          .getPropertyValue(
            '--multi-collection-header-height'
          )
      ) || 0;


    const detectionPoint =
      headerOffset + 5;


    let activeSection =
      null;


    sections.forEach(
      (section) => {
        const rect =
          section
            .getBoundingClientRect();


        if (
          rect.top <=
            detectionPoint &&
          rect.bottom >
            detectionPoint
        ) {
          activeSection =
            section;
        }
      }
    );


    if (!activeSection) {
      return;
    }


    const handle =
      activeSection
        .dataset
        .collection;


    const title =
      activeSection
        .dataset
        .title;


    if (
      !handle ||
      handle ===
        activeHandle
    ) {
      return;
    }


    activeHandle =
      handle;


    /*
     * Update browser URL without page reload.
     */

    history.replaceState(
      {},
      '',
      `/collections/${handle}`
    );


    /*
     * Update browser page title.
     */

    if (title) {
      document.title =
        title;
    }


    console.log(
      '[multi-collection] Active:',
      handle
    );
  }


  /*
   * ============================================================
   * CREATE A NEW COLLECTION SECTION
   * FROM PAGE 1
   * ============================================================
   */

  function createCollectionSection(
    handle,
    doc
  ) {
    /*
     * Collection title.
     */

    const sourceTitle =
      doc.querySelector(
        TITLE_SELECTOR
      );


    const titleText =
      sourceTitle

        ? getCleanTitle(
            sourceTitle
          )

        : handle;


    /*
     * Source Shopify grid.
     */

    const sourceGrid =
      doc.querySelector(
        GRID_SELECTOR
      );


    if (!sourceGrid) {
      throw new Error(
        `Product grid missing: ${handle}`
      );
    }


    /*
     * Create section.
     */

    const section =
      document.createElement(
        'div'
      );


    section.className =
      'multi-collection-section';


    section.dataset.collection =
      handle;


    section.dataset.title =
      titleText;


    /*
     * Sticky title.
     */

    const heading =
      document.createElement(
        'h1'
      );


    heading.className =
      'collection-hero__title multi-collection-sticky-title';


    heading.textContent =
      titleText;


    section.appendChild(
      heading
    );


    /*
     * Import Shopify grid.
     */

    const collectionGrid =
      document.importNode(
        sourceGrid,
        true
      );


    /*
     * Every grid must have a unique ID.
     */

    collectionGrid.id =
      `product-grid-${handle}`;


    section.appendChild(
      collectionGrid
    );


    /*
     * Add to feed.
     */

    feedContainer.appendChild(
      section
    );


    /*
     * Upgrade Shopify custom elements.
     */

    if (
      window.customElements &&
      typeof customElements.upgrade ===
        'function'
    ) {
      customElements.upgrade(
        section
      );
    }


    /*
     * Native image optimisation.
     */

    optimiseSectionImages(
      section
    );


    /*
     * Create registry record.
     */

    const record = {
      handle:
        handle,

      title:
        titleText,

      el:
        section,

      grid:
        collectionGrid,

      /*
       * Page 1 is already loaded.
       * Next request will be page 2.
       */

      nextPage:
        2,

      complete:
        false,

      isInitial:
        false
    };


    sectionRegistry.push(
      record
    );


    console.log(
      '[multi-collection] Started collection:',
      handle,
      'Page 1 loaded.'
    );


    return record;
  }


  /*
   * ============================================================
   * START NEXT COLLECTION
   * ============================================================
   */

  async function startNextCollection() {
    const nextHandle =
      remainingQueue.shift();


    if (!nextHandle) {
      allCollectionsLoaded =
        true;


      console.log(
        '[multi-collection] All collections loaded.'
      );


      return;
    }


    console.log(
      '[multi-collection] Starting next collection:',
      nextHandle
    );


    /*
     * Fetch ONLY page 1.
     */

    const doc =
      await fetchCollectionPage(
        nextHandle,
        1
      );


    /*
     * Create collection with page 1 only.
     */

    currentStreamRecord =
      createCollectionSection(
        nextHandle,
        doc
      );
  }


  /*
   * ============================================================
   * LOAD NEXT PAGE OF CURRENT COLLECTION
   * ============================================================
   */

  async function loadNextPageOfCurrentCollection() {
    if (!currentStreamRecord) {
      await startNextCollection();

      return;
    }


    /*
     * If current collection has already completed,
     * move to the next collection.
     */

    if (
      currentStreamRecord.complete
    ) {
      await startNextCollection();

      return;
    }


    const handle =
      currentStreamRecord.handle;


    const page =
      currentStreamRecord.nextPage;


    console.log(
      '[multi-collection] Loading page:',
      handle,
      page
    );


    /*
     * Fetch exactly ONE page.
     */

    const doc =
      await fetchCollectionPage(
        handle,
        page
      );


    /*
     * Extract product cards.
     */

    const productCards =
      getProductCardsFromDocument(
        doc
      );


    /*
     * No products means the collection is finished.
     */

    if (
      productCards.length ===
      0
    ) {
      currentStreamRecord.complete =
        true;


      console.log(
        '[multi-collection] Collection complete:',
        handle
      );


      /*
       * Immediately start page 1 of the next collection.
       *
       * This avoids requiring the customer to perform
       * another scroll after reaching an empty page.
       */

      await startNextCollection();


      return;
    }


    /*
     * Add only this page's products.
     */

    productCards.forEach(
      (productCard) => {
        currentStreamRecord
          .grid
          .appendChild(
            document.importNode(
              productCard,
              true
            )
          );
      }
    );


    /*
     * Increment next page number.
     */

    currentStreamRecord.nextPage++;


    /*
     * Upgrade any Shopify custom elements
     * inside the newly-added cards.
     */

    if (
      window.customElements &&
      typeof customElements.upgrade ===
        'function'
    ) {
      customElements.upgrade(
        currentStreamRecord.el
      );
    }


    /*
     * Apply native lazy loading and
     * asynchronous image decoding.
     */

    optimiseSectionImages(
      currentStreamRecord.el
    );


    console.log(
      '[multi-collection] Added page:',
      handle,
      page,
      `(${productCards.length} products)`
    );
  }


  /*
   * ============================================================
   * LOAD ONE NEXT CHUNK
   * ============================================================
   */

  async function loadNextChunk() {
    if (
      isLoading ||
      allCollectionsLoaded
    ) {
      return;
    }


    /*
     * The initial collection is normally allowed to finish
     * through your existing Ajaxinate system first.
     */

    if (
      !initialCollectionFinished
    ) {
      if (
        !isInitialCollectionComplete()
      ) {

        const idleForMs =
          Date.now() -
          lastGridMutationAt;


        const nativeSystemStalled =
          idleForMs >
          NATIVE_PAGINATION_STALL_MS;


        if (!nativeSystemStalled) {
          /*
           * Still give the native system a fair chance -
           * it might just be mid-request.
           */

          return;
        }


        /*
         * The native pagination system hasn't added anything
         * to the grid in a while (e.g. because infinite
         * scroll / filtering / sorting was turned off in the
         * theme). Take over pagination of the initial
         * collection ourselves so the customer never gets
         * stuck here.
         */

        isLoading =
          true;


        try {
          console.warn(
            '[multi-collection] Native pagination looks stalled - taking over initial collection pagination.'
          );


          await takeOverInitialCollectionPagination();

        } catch (error) {
          console.error(
            '[multi-collection] Fallback pagination error:',
            error
          );

        } finally {
          isLoading =
            false;
        }
      }


      initialCollectionFinished =
        true;


      const initialRecord =
        sectionRegistry[0];


      if (initialRecord) {
        initialRecord.complete =
          true;
      }


      console.log(
        '[multi-collection] Initial collection complete.'
      );
    }


    /*
     * Lock.
     */

    isLoading =
      true;


    try {
      /*
       * Load exactly one page/chunk.
       */

      await loadNextPageOfCurrentCollection();

    } catch (error) {
      console.error(
        '[multi-collection] Loading error:',
        error
      );

    } finally {
      /*
       * Unlock.
       */

      isLoading =
        false;


      /*
       * After loading, the new content might still leave
       * the customer close to the bottom.
       *
       * Recheck after layout has updated.
       */

      requestAnimationFrame(
        () => {
          checkAutoLoadDistance();
        }
      );
    }
  }


  /*
   * ============================================================
   * CHECK DISTANCE TO END OF LOADED FEED
   * ============================================================
   */

  function checkAutoLoadDistance() {
    if (
      isLoading ||
      allCollectionsLoaded ||
      !userHasScrolled
    ) {
      return;
    }


    /*
     * Last collection currently in feed.
     */

    const lastRecord =
      sectionRegistry[
        sectionRegistry.length - 1
      ];


    if (!lastRecord) {
      return;
    }


    /*
     * Distance from bottom of loaded content
     * to bottom of viewport.
     */

    const rect =
      lastRecord
        .el
        .getBoundingClientRect();


    const distanceToEnd =
      rect.bottom -
      window.innerHeight;


    /*
     * Load exactly one next chunk.
     */

    if (
      distanceToEnd <=
        PRELOAD_DISTANCE &&
      distanceToEnd >=
        -1500
    ) {
      console.log(
        '[multi-collection] Near end. Loading one chunk.'
      );


      loadNextChunk();
    }
  }


  /*
   * ============================================================
   * SCROLL LISTENER
   * ============================================================
   */

  let scrollTicking =
    false;


  window.addEventListener(
    'scroll',

    () => {
      userHasScrolled =
        true;


      if (scrollTicking) {
        return;
      }


      scrollTicking =
        true;


      requestAnimationFrame(
        () => {
          /*
           * Update header offset.
           */

          updateStickyHeaderHeight();


          /*
           * Detect active collection.
           */

          updateActiveCollection();


          /*
           * Check whether another 36-product page
           * should be loaded.
           */

          checkAutoLoadDistance();


          scrollTicking =
            false;
        }
      );
    },

    {
      passive:
        true
    }
  );


  /*
   * ============================================================
   * WATCH ORIGINAL AJAXINATE GRID
   * ============================================================
   *
   * This watches only the initial Shopify collection.
   *
   * Your existing Ajaxinate system can continue adding
   * products to the original grid.
   */

  let gridCheckTimer =
    null;


  const gridObserver =
    new MutationObserver(
      () => {

        /*
         * Record the moment the grid actually changed,
         * regardless of the debounce below - this is what
         * the stall detector in loadNextChunk() checks.
         */

        lastGridMutationAt =
          Date.now();


        clearTimeout(
          gridCheckTimer
        );


        gridCheckTimer =
          setTimeout(
            () => {
              console.log(
                '[multi-collection] Initial grid changed:',
                getInitialLoadedProductCount(),
                '/',
                initialProductTotal
              );


              /*
               * Check whether initial collection has
               * now completed.
               */

              if (
                isInitialCollectionComplete()
              ) {
                initialCollectionFinished =
                  true;


                const initialRecord =
                  sectionRegistry[0];


                if (initialRecord) {
                  initialRecord.complete =
                    true;
                }


                console.log(
                  '[multi-collection] Initial collection complete.'
                );
              }


              /*
               * Check whether the next collection
               * should start.
               */

              checkAutoLoadDistance();

            },
            400
          );
      }
    );


  gridObserver.observe(
    originalGrid,

    {
      childList:
        true
    }
  );


  /*
   * ============================================================
   * SAFETY TIMER
   * ============================================================
   *
   * This catches cases where:
   *
   * - image loading changes page height;
   * - Ajaxinate finishes between scroll events;
   * - the browser does not generate another scroll event;
   * - the native pagination system has stalled and the
   *   fallback takeover needs a chance to kick in even
   *   without a fresh scroll event.
   */

  const safetyTimer =
    setInterval(
      () => {
        if (
          !userHasScrolled ||
          allCollectionsLoaded
        ) {
          return;
        }


        updateStickyHeaderHeight();


        updateActiveCollection();


        checkAutoLoadDistance();

      },
      750
    );


  /*
   * ============================================================
   * INITIALISE
   * ============================================================
   */

  async function initialise() {
    try {

      /*
       * Detect original collection total before
       * changing the page structure.
       */

      detectInitialProductTotal();


      /*
       * Wrap original collection in the feed.
       */

      setupCurrentCollectionSection();


      /*
       * Fetch collection list and create queue.
       */

      await buildCollectionQueue();


      /*
       * Initial UI calculations.
       */

      updateStickyHeaderHeight();


      updateActiveCollection();


      /*
       * If initial collection already contains all products,
       * mark it as complete immediately.
       */

      if (
        isInitialCollectionComplete()
      ) {
        initialCollectionFinished =
          true;


        const initialRecord =
          sectionRegistry[0];


        if (initialRecord) {
          initialRecord.complete =
            true;
        }
      }


      /*
       * Start the stall clock fresh from here, so the
       * fallback takeover only considers time elapsed
       * once the page is actually ready.
       */

      lastGridMutationAt =
        Date.now();


      console.log(
        '[multi-collection] Ready.'
      );


      console.log(
        '[multi-collection] Progressive page loading enabled.'
      );


      console.log(
        '[multi-collection] Nothing will be removed from the DOM.'
      );

    } catch (error) {
      console.error(
        '[multi-collection] Initialisation error:',
        error
      );
    }
  }


  initialise();

})();